import React from 'react';
import toast from 'react-hot-toast';
import { fmt } from '../../../../lib/formatters';
import RoomQuotationTemplate from '../../../../utils/RoomQuotationTemplate';
import { generateRoomQuotationPdf } from '../../../../utils/generateRoomQuotationPdf';
import { exportToExcel } from '../../../../utils/exportUtils';

const QuotationPreview = ({ initialData, roomItems, calculateRoomTotal, calculateGrandTotal, onCancel, onSubmit, loading, isFinal = false, notesValue }) => {

  const getTemplateData = () => {
    const roomsData = Object.keys(roomItems)
      .filter(room => roomItems[room] && roomItems[room].length > 0)
      .map(room => {
        const flattenedItems = [];
        roomItems[room].forEach(block => {
          const details = block.measurement ? [
            `Particulars: ${block.measurement.particulars || '-'}`,
            `Zone: ${block.measurement.zone || '-'}`, 
            `Dims: H:${block.measurement.height} W:${block.measurement.width} D:${block.measurement.depth}`
          ] : [];

          const catNames = {
            priceListItems: 'Item',
            blumItems: 'Hardware',
            customItems: 'Custom'
          };

          ['priceListItems', 'blumItems', 'customItems'].forEach(cat => {
            (block[cat] || []).forEach(item => {
              if (cat === 'priceListItems' && item.description?.trim()) {
                // For Price List Items, format the name to include internal/shutter types and dimensions
                const nameStr = `${item.description} - ${item.internalType || 'N/A'} - ${item.shutterType || 'N/A'} [${item.width || 0}x${item.height || 0}x${item.depth || 0}]`;
                flattenedItems.push({
                  name: nameStr,
                  colour: '-',
                  type: catNames[cat] || '',
                  warranty: '-',
                  amount: fmt(parseFloat(item.amount || 0)),
                  details: details,
                  quantity: 1
                });
              } else if (item.name?.trim()) {
                flattenedItems.push({
                  name: item.name,
                  colour: '-',
                  type: catNames[cat] || '',
                  warranty: '-',
                  amount: fmt(parseFloat(item.amount || 0)),
                  details: details,
                  quantity: 1
                });
              }
            });
          });
        });

        return {
          roomName: room,
          items: flattenedItems
        };
      });

    return {
      projectName: initialData?.projectName || "-",
      clientName: initialData?.customerName || `${initialData?.firstName || ''} ${initialData?.lastName || ''}`.trim() || "-",
      date: new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }),
      site: initialData?.city || initialData?.location || "-",
      quotationNo: initialData?.customId ? `VK/${initialData.customId.toUpperCase()}` : `VK/${Date.now().toString().slice(-6)}`,
      contactRef: initialData?.phoneNumber || initialData?.phone || "-",
      designer: initialData?.projectManager?.name || initialData?.assignedTo?.name || "-",
      rooms: roomsData,
      totals: {
        total: fmt(calculateGrandTotal()),
        discount: "0",
        grandTotal: fmt(calculateGrandTotal())
      }
    };
  };

  // Build flat Excel rows from all room blocks
  const buildExcelRows = () => {
    const clientName = initialData?.customerName || `${initialData?.firstName || ''} ${initialData?.lastName || ''}`.trim() || '-';
    const quotationNo = initialData?.customId ? `VK/${initialData.customId.toUpperCase()}` : '-';
    const rows = [];

    Object.keys(roomItems).forEach(room => {
      (roomItems[room] || []).forEach(block => {
        const zone = block.measurement?.zone || '';
        const particulars = block.measurement?.particulars || '';
        const measW = block.measurement?.width || '';
        const measH = block.measurement?.height || '';
        const measD = block.measurement?.depth || '';

        // Price list items (kitchen items)
        (block.priceListItems || []).forEach(item => {
          if (item.description?.trim()) {
            rows.push({
              'Quotation No':    quotationNo,
              'Client Name':     clientName,
              'Room / Kitchen':  room,
              'Zone':            zone,
              'Particulars':     particulars,
              'Type':            'Kitchen Item',
              'Description':     item.description || '-',
              'Internal Type':   item.internalType || '-',
              'External/Shutter Type': item.shutterType || '-',
              'Width (W)':       item.width !== undefined && item.width !== '' ? item.width : measW,
              'Height (H)':      item.height !== undefined && item.height !== '' ? item.height : measH,
              'Depth (D)':       item.depth !== undefined && item.depth !== '' ? item.depth : measD,
              'Final Price (₹)': parseFloat(item.amount || 0),
            });
          }
        });

        // Blum / Hardware items
        (block.blumItems || []).forEach(item => {
          if (item.name?.trim()) {
            rows.push({
              'Quotation No':    quotationNo,
              'Client Name':     clientName,
              'Room / Kitchen':  room,
              'Zone':            zone,
              'Particulars':     particulars,
              'Type':            'Hardware',
              'Description':     item.name,
              'Internal Type':   '-',
              'External/Shutter Type': '-',
              'Width (W)':       '',
              'Height (H)':      '',
              'Depth (D)':       '',
              'Final Price (₹)': parseFloat(item.amount || 0),
            });
          }
        });

        // Custom items
        (block.customItems || []).forEach(item => {
          if (item.name?.trim()) {
            rows.push({
              'Quotation No':    quotationNo,
              'Client Name':     clientName,
              'Room / Kitchen':  room,
              'Zone':            zone,
              'Particulars':     particulars,
              'Type':            'Custom',
              'Description':     item.name,
              'Internal Type':   '-',
              'External/Shutter Type': '-',
              'Width (W)':       '',
              'Height (H)':      '',
              'Depth (D)':       '',
              'Final Price (₹)': parseFloat(item.amount || 0),
            });
          }
        });
      });
    });

    // Totals row
    if (rows.length > 0) {
      rows.push({
        'Quotation No':    '',
        'Client Name':     '',
        'Room / Kitchen':  '',
        'Zone':            '',
        'Particulars':     '',
        'Type':            '',
        'Description':     'GRAND TOTAL',
        'Internal Type':   '',
        'External/Shutter Type': '',
        'Width (W)':       '',
        'Height (H)':      '',
        'Depth (D)':       '',
        'Final Price (₹)': calculateGrandTotal(),
      });
    }

    return rows;
  };

  const handleDownloadAndSubmit = async () => {
    try {
      // Export Excel with all material/kitchen info
      const excelRows = buildExcelRows();
      if (excelRows.length > 0) {
        const clientName = initialData?.customerName || `${initialData?.firstName || ''} ${initialData?.lastName || ''}`.trim() || 'Client';
        const filename = `Quotation_${clientName}_${new Date().toLocaleDateString('en-IN').replace(/\//g, '-')}`;
        await exportToExcel(excelRows, {
          filename,
          sheetName: 'Quotation Items',
        });
        toast.success('Excel downloaded!');
      }

      onSubmit(); // Proceed to submit to backend
    } catch (err) {
      console.error(err);
      toast.error("Failed to save quotation");
    }
  };

  const templateData = getTemplateData();

  return (
    <div className="flex flex-col gap-5 p-1 bg-slate-50">
      <div className="space-y-4 h-[420px] overflow-y-auto pr-2 custom-scrollbar">
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-[12px] font-bold text-slate-700 uppercase tracking-widest">
            {isFinal ? 'FINAL' : 'INITIAL'} QUOTATION PREVIEW
          </h3>
        </div>

        <div className="border border-slate-200 rounded-xl shadow-inner bg-slate-200/50 p-2 sm:p-4 w-full">
          <div style={{ zoom: '0.8' }}>
            <div className="bg-white shadow-xl mx-auto w-[800px]">
              <RoomQuotationTemplate data={templateData} />
            </div>
          </div>
        </div>
      </div>


      <div className="flex flex-col gap-3 pt-3 border-t border-slate-200 mt-2">
        <div className="flex flex-col-reverse sm:flex-row items-center gap-3 w-full justify-between">
          <button
            type="button"
            onClick={onCancel}
            className="w-full sm:w-auto px-6 cursor-pointer py-2.5 border border-slate-300 bg-white hover:bg-slate-50 text-slate-800 rounded-md text-[12px] font-semibold transition-all shadow-sm"
          >
            Cancel
          </button>
          <div className="flex-1 flex gap-3 w-full justify-end">
            <button
              type="button"
              onClick={onCancel}
              className="w-full sm:w-auto px-6 cursor-pointer py-2.5 border border-slate-200 bg-white hover:bg-slate-50 text-slate-800 rounded-md text-[12px] font-bold transition-all shadow-sm"
            >
              Update
            </button>
            <button
              type="button"
              onClick={handleDownloadAndSubmit}
              disabled={loading}
              className="w-full sm:w-auto px-8 cursor-pointer py-2.5 min-h-[38px] bg-slate-900 hover:bg-black text-white rounded-md text-[12px] font-bold transition-all shadow-sm flex items-center justify-center gap-2 disabled:opacity-70"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuotationPreview;
